'use client';

import { usePathname } from 'next/navigation';
import { ReactNode, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface PageTransitionProps {
  children: ReactNode;
  isLoaded?: boolean;
}

const PageTransition = ({ children, isLoaded = false }: PageTransitionProps) => {
  const pathname = usePathname();
  const isHome = pathname === '/';
  const [showLoader, setShowLoader] = useState(false);
  
  // İlerleme çubuğunun görünürlüğünü kontrol etmek için
  useEffect(() => {
    if (isLoaded) {
      // Sayfa yüklendiğinde yükleme çubuğunu göster
      setShowLoader(true);
      
      // Sayfa tam olarak yüklendikten 800ms sonra yükleme çubuğunu gizle
      const timer = setTimeout(() => {
        setShowLoader(false);
      }, 800);
      
      return () => clearTimeout(timer);
    } else {
      setShowLoader(false);
    }
  }, [isLoaded, pathname]);
  
  // Sayfa değişimlerinde yeni içerik görüntülendiğinde ekranı yukarı kaydır
  useEffect(() => {
    if (isLoaded) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [pathname, isLoaded]);
  
  const routeProgress = (
    <AnimatePresence>
      {showLoader && (
        <motion.div
          className="fixed top-0 left-0 right-0 h-1 bg-primary z-[60]"
          initial={{ scaleX: 0, originX: 0 }}
          animate={{
            scaleX: 1,
            transition: { duration: 0.6, ease: 'easeOut' },
          }}
          exit={{
            scaleX: 0,
            opacity: 0,
            transition: { duration: 0.3, ease: 'easeInOut' },
          }}
        />
      )}
    </AnimatePresence>
  );

  /* Ana sayfa: motion/transform yok — fixed hero oku ve tam genişlik slayt WebKit’te bozulmuyor */
  return (
    <AnimatePresence mode="wait">
      {isHome ? (
        <div key="home" className="flex flex-col w-full">
          {children}
          {routeProgress}
        </div>
      ) : (
        <motion.div
          key={pathname}
          initial={{ opacity: 0, y: 10 }}
          animate={{
            opacity: 1,
            y: 0,
            transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
          }}
          exit={{
            opacity: 0,
            y: -10,
            transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1] },
          }}
          className="flex flex-col w-full"
        >
          <div className="container mx-auto px-4 py-4 md:py-6">{children}</div>
          {routeProgress}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PageTransition; 